import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useDiagnosticTest,
  useDiagnosticTests,
  useAllDiagnosticRooms,
  useActiveAccessories,
  useActiveSpecimenTypes,
  useActiveDiagnosticCategories,
  useCreateDiagnosticTest,
  useUpdateDiagnosticTest,
} from '../../services/httpServices/queries';
import { generateAcronymCode, makeUniqueCode } from '../../utils/code';
import { ValidatedNumberInput } from '../../components/ui/validated-number-input';
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  Plus,
  Save,
  Check,
  X,
  FlaskConical,
} from 'lucide-react';

export function DiagnosticTestForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;

  const { data: testData, isLoading: testLoading, error: testError } = useDiagnosticTest(id);
  const { data: roomsData, isLoading: roomsLoading } = useAllDiagnosticRooms();
  const { data: accessoriesData, isLoading: accessoriesLoading } = useActiveAccessories();
  const { data: specimenTypesData, isLoading: specimenTypesLoading } = useActiveSpecimenTypes();
  const { data: categoriesData, isLoading: categoriesLoading } = useActiveDiagnosticCategories();
  const createMutation = useCreateDiagnosticTest();
  const updateMutation = useUpdateDiagnosticTest();

  // All existing test codes, used to keep an auto-generated code unique.
  const { data: allTestsData } = useDiagnosticTests(1, 1000);

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    categoryId: '',
    price: 0,
    discountPercent: 0,
    turnaroundTime: '' as number | '',
    preparationInstructions: '',
    diagnosticRoomId: '',
    containerAccessoryId: '',
    requiredVolume: '',
    specimenTypeId: '',
    accessoryIds: [] as string[],
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [codeManuallyEdited, setCodeManuallyEdited] = useState(false);

  const categories = categoriesData?.data || [];
  const accessories = (accessoriesData?.data || []) as any[];
  const specimenTypes = specimenTypesData?.data || [];
  const test = testData?.data;
  const existingCodes = useMemo(
    () => (allTestsData?.data || []).map((item: any) => item.code).filter(Boolean) as string[],
    [allTestsData],
  );

  // Auto-generate the code from the name (acronym), kept unique among existing
  // tests and left editable — editing the code stops further auto-generation.
  useEffect(() => {
    if (isEdit) return;
    if (!formData.name.trim()) return;
    if (codeManuallyEdited) return;
    const base = generateAcronymCode(formData.name, 20);
    if (!base) return;
    const unique = makeUniqueCode(base, existingCodes, { maxLength: 20 });
    setFormData((prev) => (prev.code === unique ? prev : { ...prev, code: unique }));
  }, [formData.name, codeManuallyEdited, isEdit, existingCodes]);

  useEffect(() => {
    if (isEdit && test) {
      const links = test.accessories || [];
      const containerLink = links.find((link: any) => link.isSpecimenContainer);
      setFormData({
        code: test.code,
        name: test.name,
        categoryId: test.categoryId || test.diagnosticCategory?.id || '',
        price: test.price,
        discountPercent: test.discountPercent || 0,
        turnaroundTime: test.turnaroundTime ?? '',
        preparationInstructions: test.preparationInstructions || '',
        diagnosticRoomId: test.diagnosticRoomId || test.room?.id || '',
        containerAccessoryId: containerLink?.diagnosticAccessoryId || containerLink?.accessory?.id || '',
        requiredVolume: containerLink?.requiredVolume || '',
        specimenTypeId: containerLink?.accessory?.specimenType?.id || '',
        accessoryIds: links
          .filter((link: any) => !link.isSpecimenContainer)
          .map((link: any) => link.diagnosticAccessoryId || link.accessory?.id || link.id),
      });
      setCodeManuallyEdited(true);
    }
  }, [isEdit, test]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.code.trim()) errors.code = 'Code is required';
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.categoryId) errors.categoryId = 'Category is required';
    if (formData.price < 0) errors.price = 'Price must be positive';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload: any = {
      code: formData.code,
      name: formData.name,
      categoryId: formData.categoryId,
      price: formData.price,
      discountPercent: formData.discountPercent,
      turnaroundTime: formData.turnaroundTime === '' ? undefined : formData.turnaroundTime,
      preparationInstructions: formData.preparationInstructions || undefined,
      diagnosticRoomId: formData.diagnosticRoomId || undefined,
      containerAccessoryId: formData.containerAccessoryId || undefined,
      requiredVolume: formData.requiredVolume || undefined,
      accessoryIds: formData.accessoryIds,
    };

    try {
      if (isEdit) {
        await updateMutation.mutateAsync({ id: id!, data: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      navigate('/administration/diagnostic-tests');
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Something went wrong';
      setFormErrors({ general: message });
    }
  };

  const updateField = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'code') {
      setCodeManuallyEdited(String(value).trim() !== '');
    }
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  // Exactly one linked item may be the specimen container. Selecting a container
  // item sets it (and removes it from the plain-accessory list); selecting it
  // again clears the container. The Sample Type selector mirrors the container's
  // specimen type, so the two stay in sync.
  const toggleContainer = (accessoryId: string) => {
    setFormData((prev) => {
      const clearing = prev.containerAccessoryId === accessoryId;
      const accessory = accessories.find((acc: any) => acc.id === accessoryId);
      return {
        ...prev,
        containerAccessoryId: clearing ? '' : accessoryId,
        specimenTypeId: clearing ? '' : accessory?.specimenTypeId || accessory?.specimenType?.id || '',
        accessoryIds: prev.accessoryIds.filter((existing) => existing !== accessoryId),
      };
    });
  };

  // Sample Type is the user-facing entry point: choosing one auto-selects the
  // matching container item (single source of truth stays on the container link).
  const handleSampleTypeChange = (specimenTypeId: string) => {
    setFormData((prev) => {
      if (!specimenTypeId) {
        return { ...prev, specimenTypeId: '', containerAccessoryId: '' };
      }
      const match = accessories.find(
        (acc: any) => acc.isContainer && (acc.specimenTypeId || acc.specimenType?.id) === specimenTypeId,
      );
      return {
        ...prev,
        specimenTypeId,
        containerAccessoryId: match?.id || '',
        accessoryIds: match ? prev.accessoryIds.filter((existing) => existing !== match.id) : prev.accessoryIds,
      };
    });
  };

  const toggleAccessory = (accessoryId: string) => {
    setFormData((prev) => ({
      ...prev,
      accessoryIds: prev.accessoryIds.includes(accessoryId)
        ? prev.accessoryIds.filter((existing) => existing !== accessoryId)
        : [...prev.accessoryIds, accessoryId],
    }));
  };

  const inputBase = 'w-full px-3 py-2 border rounded-lg bg-background text-text focus:outline-none focus:ring-2 text-sm transition-colors';
  const inputValid = 'border-border focus:ring-primary';
  const inputInvalid = 'border-danger focus:ring-danger';
  const labelClass = 'block text-sm font-medium text-text mb-1';

  const ic = (field: string) => `${inputBase} ${formErrors[field] ? inputInvalid : inputValid}`;
  const err = (field: string) => formErrors[field] ? <p className="text-xs text-danger mt-1">{formErrors[field]}</p> : null;

  if (isEdit && testLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isEdit && (testError || !test)) {
    return (
      <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
        <AlertCircle size={18} />
        <span>Failed to load diagnostic test details. Please try again.</span>
      </div>
    );
  }

  const hasContainer = !!formData.containerAccessoryId;
  const sampleTypeHasContainer = !formData.specimenTypeId || accessories.some(
    (acc: any) => acc.isContainer && (acc.specimenTypeId || acc.specimenType?.id) === formData.specimenTypeId,
  );

  return (
    <div className="w-full space-y-4">
      <button
        onClick={() => navigate('/administration/diagnostic-tests')}
        className="inline-flex items-center gap-2 text-text-muted hover:text-text transition-colors text-sm cursor-pointer"
      >
        <ArrowLeft size={18} />
        Back to Diagnostic Tests
      </button>

      <div className="flex items-center gap-3">
        <FlaskConical className="w-6 h-6 text-primary" />
        <h1 className="text-2xl font-bold text-text">
          {isEdit ? 'Edit Diagnostic Test' : 'Add Diagnostic Test'}
        </h1>
      </div>

      {formErrors.general && (
        <div className="bg-danger/10 text-danger rounded-lg p-4 flex items-center gap-2">
          <AlertCircle size={18} />
          <span className="text-sm font-medium">{formErrors.general}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-surface border border-border rounded-xl p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={labelClass}>Name *</label>
            <input
              type="text"
              className={ic('name')}
              value={formData.name}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder="Complete Blood Count"
            />
            {err('name')}
          </div>
          <div>
            <label className={labelClass}>
              Code * {!isEdit && <span className="text-text-muted font-normal">(auto-generated)</span>}
            </label>
            <input
              type="text"
              className={`${ic('code')} font-mono`}
              value={formData.code}
              onChange={(e) => updateField('code', e.target.value)}
              placeholder="CBC"
            />
            {err('code')}
          </div>
          <div>
            <label className={labelClass}>Category *</label>
            <select
              className={ic('categoryId')}
              value={formData.categoryId}
              onChange={(e) => updateField('categoryId', e.target.value)}
              disabled={categoriesLoading}
            >
              <option value="">{categoriesLoading ? 'Loading...' : 'Select Category'}</option>
              {categories.map((cat: any) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
            {err('categoryId')}
          </div>
          <div>
            <label className={labelClass}>Price (৳) *</label>
            <ValidatedNumberInput
              className={ic('price')}
              value={formData.price}
              onChange={(v) => updateField('price', v)}
            />
            {err('price')}
          </div>
          <div>
            <label className={labelClass}>Discount %</label>
            <ValidatedNumberInput
              max={100}
              className={`${inputBase} ${inputValid}`}
              value={formData.discountPercent}
              onChange={(v) => updateField('discountPercent', v)}
              placeholder="0"
            />
          </div>
          <div>
            <label className={labelClass}>Turnaround Time (hours)</label>
            <ValidatedNumberInput
              className={`${inputBase} ${inputValid}`}
              value={formData.turnaroundTime}
              onChange={(v) => updateField('turnaroundTime', v || '')}
              placeholder="e.g. 2"
            />
          </div>
          <div>
            <label className={labelClass}>Room</label>
            <select
              className={`${inputBase} ${inputValid}`}
              value={formData.diagnosticRoomId}
              onChange={(e) => updateField('diagnosticRoomId', e.target.value)}
              disabled={roomsLoading}
            >
              <option value="">{roomsLoading ? 'Loading...' : 'Select Room'}</option>
              {(roomsData?.data || []).map((room: any) => (
                <option key={room.id} value={room.id}>{room.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Sample Type</label>
            <select
              className={`${inputBase} ${inputValid}`}
              value={formData.specimenTypeId}
              onChange={(e) => handleSampleTypeChange(e.target.value)}
              disabled={specimenTypesLoading}
            >
              <option value="">{specimenTypesLoading ? 'Loading...' : 'No Sample (Procedure)'}</option>
              {specimenTypes
                .filter((type: any) => type.code !== 'NONE')
                .map((type: any) => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
            </select>
            {!sampleTypeHasContainer && (
              <p className="text-xs text-warning mt-1">
                No active container item for this sample type. Add one under Items first.
              </p>
            )}
          </div>
          <div>
            <label className={labelClass}>Required Volume</label>
            <input
              type="text"
              className={`${inputBase} ${inputValid}`}
              value={formData.requiredVolume}
              onChange={(e) => updateField('requiredVolume', e.target.value)}
              placeholder="e.g. 3 mL (optional override)"
              disabled={!hasContainer}
            />
          </div>
        </div>

        <div
          className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${
            hasContainer
              ? 'bg-success/5 border-success/20 text-success'
              : 'bg-background border-border text-text-muted'
          }`}
        >
          <FlaskConical size={16} />
          {hasContainer
            ? 'This test collects a physical specimen (a tube label will be printed).'
            : 'This test does not collect a specimen (procedure-only, e.g. ECG / X-ray).'}
        </div>

        <div>
          <label className={labelClass}>Preparation Instructions</label>
          <textarea
            className={`${inputBase} ${inputValid}`}
            value={formData.preparationInstructions}
            onChange={(e) => updateField('preparationInstructions', e.target.value)}
            rows={2}
            placeholder="Fasting required..."
          />
        </div>

        <div>
          <label className={labelClass}>Items</label>
          <p className="text-xs text-text-muted mb-2">
            Pick consumables to bill with this test. Items marked <span className="inline-flex items-center gap-1 font-medium text-primary"><FlaskConical size={12} />Container</span> can
            be set as the specimen container — choose at most one.
          </p>
          <div className="flex flex-wrap gap-2">
            {accessoriesLoading ? (
              <Loader2 size={16} className="animate-spin text-text-muted" />
            ) : (
              <>
                {accessories.map((acc: any) => {
                  const isContainer = !!acc.isContainer;
                  const isSelected = isContainer
                    ? formData.containerAccessoryId === acc.id
                    : formData.accessoryIds.includes(acc.id);
                  return (
                    <button
                      key={acc.id}
                      type="button"
                      onClick={() => (isContainer ? toggleContainer(acc.id) : toggleAccessory(acc.id))}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                        isSelected
                          ? 'bg-primary text-white border-primary'
                          : 'bg-background text-text-muted border-border hover:text-text'
                      }`}
                      title={isContainer ? 'Use as specimen container' : 'Bill as a consumable'}
                    >
                      {isSelected && <Check size={12} className="inline mr-1" />}
                      {isContainer && <FlaskConical size={12} className="inline mr-1" />}
                      {acc.name} (৳{acc.price})
                      {isContainer && acc.color ? ` · ${acc.color}` : ''}
                    </button>
                  );
                })}
                {accessories.length === 0 && (
                  <p className="text-xs text-text-muted">No active items available. Add items first.</p>
                )}
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 pt-4 border-t border-border">
          <button
            type="button"
            onClick={() => navigate('/administration/diagnostic-tests')}
            className="px-4 py-2 text-text-muted hover:text-text transition-colors text-sm flex items-center gap-2"
          >
            <X size={16} />
            Cancel
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending || updateMutation.isPending}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {(createMutation.isPending || updateMutation.isPending) && <Loader2 size={16} className="animate-spin" />}
            {isEdit ? <Save size={16} /> : <Plus size={16} />}
            {isEdit ? 'Save Changes' : 'Add Test'}
          </button>
        </div>
      </form>
    </div>
  );
}
